/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";

import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Fade from "@mui/material/Fade";
import { putItemList } from "../features/item/item-list/putItemListSlice";
import ToppingCustomization from "../features/topping-customization/ToppingCustomization";
import { postCartItems } from "../features/cart/CartSlice";
import Toppings from "../features/topping-customization/Toppings";
import { getContentHeight } from "../utils/utils";

function listToMatrix(list, elementsPerSubArray) {
  const matrix = [];
  let i;
  let k;
  for (i = 0, k = -1; i < list.length; i += 1) {
    if (i % elementsPerSubArray === 0) {
      k += 1;
      matrix[k] = [];
    }
    matrix[k].push(list[i]);
  }
  return matrix.reverse();
}

/* TODO: Reoder ItemCustomization to fix issue.Till then treat it as warning */
function ItemCustomization() {
  const [menuHeight, setMenuHeight] = useState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleWindowSizeChange = () => {
    setMenuHeight(getContentHeight());
  };

  const itemCustomizationStyle = {
    overflowY: "auto",
    overflowX: "hidden",
    width: "100vw",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, max-content))",
    display: "flex",
    flexGrow: 1,
    maxHeight: menuHeight,
    marginTop: "49px",
  };

  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);
  let itemDetails;
  try {
    itemDetails = JSON.parse(localStorage.getItem(location.pathname));
  } catch (error) {
    console.error("Failed to parse itemDetails", error);
  }
  if (itemDetails.toppings == null) {
    itemDetails.toppings = [];
  }
  console.log("ItemCustomization data from local storage", itemDetails);
  const [selectedToppings, setTopping] = useState(itemDetails.toppings);
  const updateToppingList = (topping, isAdded) => {
    const modifiedToppings = JSON.parse(JSON.stringify(selectedToppings));
    const index = modifiedToppings.findIndex(
      (toppingToRemove) => toppingToRemove.item_id === topping.item_id
    );
    if (isAdded) {
      modifiedToppings[index].quantity = 1;
      setTopping(modifiedToppings);
    } else {
      modifiedToppings[index].quantity = 0;
      setTopping(modifiedToppings);
    }
  };
  useEffect(() => {
    itemDetails.toppings = selectedToppings;
    console.log("itemDetails", itemDetails);
    const putPayload = {
      items: [itemDetails],
      view: "ITEM_VIEW",
    };
    dispatch(putItemList(putPayload));
  }, [itemDetails, itemDetails.toppings]);

  console.log("Supported toppings: ", itemDetails.toppings);
  console.log("Selected toppings: ", selectedToppings);
  const toppings = itemDetails.toppings
    ? listToMatrix(
        itemDetails.toppings,
        itemDetails.toppings.length < 12
          ? 4
          : itemDetails.toppings.length / 3 + 1
      )
    : [];
  const totalCost = (
    selectedToppings
      .filter((topping) => topping.quantity === 1)
      .map((topping) => topping.price)
      .reduce((prev, next) => prev + next, 0) + itemDetails.price
  ).toFixed(2);
  const totalCalorieCost = (
    selectedToppings
      .filter((topping) => topping.quantity === 1)
      .map((topping) => topping.calories)
      .reduce((prev, next) => prev + next, 0) + itemDetails.calories
  ).toFixed(0);
  const itemData = {
    image_loc: itemDetails.image_loc,
    item_id: itemDetails.item_id,
    name: itemDetails.name,
    price: itemDetails.price,
    size: itemDetails.size,
    toppings: selectedToppings,
    calories: itemDetails.calories,
    description: itemDetails.description,
  };
  console.log("itemData", itemData);
  const addToCartHandler = () => {
    console.debug("Adding to cart", itemData);
    dispatch(postCartItems(itemData));
    navigate(-1);
  };
  return (
    <Grid
      sx={itemCustomizationStyle}
      container
      justifyContent='center'
      alignItems='center'
    >
      <Grid
        container
        spacing={0}
        direction='row'
        alignItems='center'
        justifyContent='center'
      >
        <Grid item xs={12} md={6} lg={6} align='center'>
          <Fade style={{ transformOrigin: "0 0 0" }} timeout={500} in appear>
            <Box
              component='img'
              sx={{
                maxWidth: { xs: 400, md: 400 },
                maxHeight: { xs: 350, md: 350 },
                padding: 2,
                background:
                  "radial-gradient(51.13% 22.52% at 50% 77.25%, #000000 0%, rgba(0, 0, 0, 0) 100%)",
              }}
              alt='Food'
              // eslint-disable-next-line global-require
              src={`/${itemDetails.image_loc}`}
            />
          </Fade>
        </Grid>
        <Grid item xs={12} md={6} lg={6} align='center'>
          <Fade style={{ transformOrigin: "0 0 0" }} timeout={500} in appear>
            <Card
              sx={{
                maxWidth: "90%",
                background: "rgba(66, 66, 66, 0.5)",
                border: "1px solid #616161",
                backdropFilter: "blur(8px)",
                borderRadius: "8px",
              }}
            >
              <CardHeader
                title={`${itemDetails.name}, ${itemDetails.size}`}
                subheader={itemDetails.description}
                sx={{ textAlign: "left" }}
              />
              <CardContent>
                <Box
                  sx={{
                    textAlign: "left",
                  }}
                >
                  {" "}
                  <Typography
                    variant='caption'
                    sx={{ marginBottom: 0.5, color: "#b2b5b9" }}
                  >
                    {`Estimated ${totalCalorieCost} Calories`}
                  </Typography>
                  <Typography variant='body1' sx={{ marginBottom: 0.5 }}>
                    <Toppings
                      toppings={
                        selectedToppings
                          ? selectedToppings
                              .filter((topping) => topping.quantity === 1)
                              .map((topping) => topping)
                          : []
                      }
                    />
                  </Typography>
                  <Divider light />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      lineHeight: 2.5,
                    }}
                  >
                    <Typography
                      gutterBottom
                      variant='p'
                      color='primary.contrastText'
                    >
                      Total
                    </Typography>
                    <Typography variant='h4' color='primary.contrastText'>
                      {`$${totalCost}`}
                    </Typography>
                  </Box>
                </Box>
                <Divider light />
              </CardContent>
              <CardActions
                disableSpacing
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant='outlined'
                  sx={{ m: 1 }}
                  onClick={() => navigate(-1)}
                >
                  Cancel Item
                </Button>
                <Button
                  variant='contained'
                  sx={{ m: 1 }}
                  onClick={addToCartHandler}
                >
                  Add to Order
                </Button>
              </CardActions>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={0}
        direction='column'
        alignItems='center'
        justifyContent='center'
        align='center'
      >
        <Divider sx={{ width: "97%", border: "1px solid #616161", m: 2 }} />
      </Grid>
      <Grid container sx={{ width: "100vw" }}>
        {toppings.map((toppingArr, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid item xs={6} md={6} lg={6} key={index}>
            {toppingArr.map((topping) => (
              <ToppingCustomization
                name={topping.name}
                item_id={topping.item_id}
                quantity={topping.quantity}
                topping={topping}
                updateToppingList={updateToppingList}
                key={topping.name + topping.item_id}
              />
            ))}
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}

export default ItemCustomization;
